import { createContext, useContext, useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import ProjectPage from "@/pages/ProjectPage";
import AspectRatioPage from "@/pages/AspectRatioPage";
import RestripePage from "@/pages/RestripePage";
import EncodingPage from "@/pages/EncodingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import CropWorkspacePage from "@/pages/CropWorkspacePage";
import AdminPage from "@/pages/AdminPage";
import SharedPage from "@/pages/SharedPage";
import ClientReviewPage from "@/pages/ClientReviewPage";
import AuthCallback from "@/pages/AuthCallback";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults — use Bearer tokens instead of cookies (avoids CORS+credentials issues with K8s ingress)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config._retry) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        error.config._retry = true;
        try {
          const res = await axios.post(`${API}/auth/refresh`, {}, {
            headers: { Cookie: `refresh_token=${refreshToken}` },
          });
          if (res.data.access_token) {
            localStorage.setItem("access_token", res.data.access_token);
            error.config.headers.Authorization = `Bearer ${res.data.access_token}`;
            return axios(error.config);
          }
        } catch {}
      }
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Format API error helper
export const formatApiError = (error) => {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (e) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data.access_token) localStorage.setItem("access_token", response.data.access_token);
    if (response.data.refresh_token) localStorage.setItem("refresh_token", response.data.refresh_token);
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    if (response.data.access_token) localStorage.setItem("access_token", response.data.access_token);
    if (response.data.refresh_token) localStorage.setItem("refresh_token", response.data.refresh_token);
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="fixed inset-0 bg-gradient-hero -z-10" />
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-sm bg-cyan-500/20 animate-pulse-glow" />
          <p className="text-cyan-200/40 font-['Rajdhani'] tracking-widest text-sm">LOADING...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Main App Component
function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id from Google OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/shared/:shareToken" element={<SharedPage />} />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId" element={
              <ProtectedRoute>
                <ProjectPage />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId/aspect-ratio" element={
              <ProtectedRoute>
                <AspectRatioPage />
              </ProtectedRoute>
            } />
            <Route path="/crop-workspace" element={
              <ProtectedRoute>
                <CropWorkspacePage />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId/restripe" element={
              <ProtectedRoute>
                <RestripePage />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId/encoding" element={
              <ProtectedRoute>
                <EncodingPage />
              </ProtectedRoute>
            } />
            <Route path="/payment/success" element={
              <ProtectedRoute>
                <PaymentSuccessPage />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute adminOnly>
                <AdminPage />
              </ProtectedRoute>
            } />
            
            {/* Public review page — no auth required */}
            <Route path="/review/:token" element={<ClientReviewPage />} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen bg-background">
          <AppRouter />
          <Toaster position="bottom-right" />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
